// frontend/src/components/EmailHistoryModal.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getLeadEmailHistory } from '../api';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/Button';
import { Spinner } from './ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { MailOpen, RefreshCw, XCircle } from 'lucide-react';

// Global cache for email history data
const emailHistoryCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Request tracking to prevent duplicate requests
const pendingRequests = new Map();
const REQUEST_DEBOUNCE_TIME = 2000; // 2 seconds

const EmailHistoryModal = ({ leadId, isOpen, onClose, leadName }) => {
  const [emailHistory, setEmailHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const lastFetchTime = useRef(0);
  const fetchTimeoutRef = useRef(null);
  const componentMounted = useRef(true);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      componentMounted.current = false;
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  const fetchEmailHistory = useCallback(async (forceRefresh = false) => {
    if (!leadId) return;
    
    // Clear any existing timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    // Check if we have cached data that's still valid
    const now = Date.now();
    const cachedData = emailHistoryCache.get(leadId);
    const isCacheValid = cachedData && (now - cachedData.timestamp < CACHE_DURATION);
    
    // If we have valid cached data and aren't forcing a refresh, use it
    if (isCacheValid && !forceRefresh) {
      console.log(`Using cached email history for lead ${leadId}`);
      if (componentMounted.current) {
        setEmailHistory(cachedData.data);
      }
      return;
    }
    
    // Check if there's already a pending request for this lead
    if (pendingRequests.has(leadId) && !forceRefresh) {
      console.log(`Request for lead ${leadId} already in progress, skipping`);
      return;
    }
    
    // Prevent rapid refetching (debounce)
    const timeSinceLastFetch = now - lastFetchTime.current;
    if (timeSinceLastFetch < REQUEST_DEBOUNCE_TIME && !forceRefresh) {
      console.log(`Debouncing email history fetch for lead ${leadId}`);
      fetchTimeoutRef.current = setTimeout(() => {
        fetchEmailHistory(forceRefresh);
      }, REQUEST_DEBOUNCE_TIME - timeSinceLastFetch);
      return;
    }
    
    if (componentMounted.current) {
      setLoading(true);
      setError('');
    }
    lastFetchTime.current = now;
    
    // Mark this request as pending
    pendingRequests.set(leadId, true);
    
    try {
      console.log(`Fetching email history for lead ${leadId}...`);
      const result = await getLeadEmailHistory(leadId);
      console.log("Email history API response:", result);
      
      if (componentMounted.current) {
        if (result.success) {
          // Sort by sent_at date, newest first
          const sortedHistory = (result.data || []).sort((a, b) => {
            return new Date(b.sent_at) - new Date(a.sent_at);
          });
          
          console.log(`Received ${sortedHistory.length} email records`);
          setEmailHistory(sortedHistory);
          
          // Update cache
          emailHistoryCache.set(leadId, {
            data: sortedHistory,
            timestamp: now
          });
        } else {
          console.error('Failed to fetch email history:', result.message);
          setError('Failed to load email history');
        }
      }
    } catch (err) {
      console.error('Error fetching email history:', err);
      if (componentMounted.current) {
        setError('Failed to load email history. Please try again.');
      }
    } finally {
      if (componentMounted.current) {
        setLoading(false);
      }
      // Remove from pending requests
      pendingRequests.delete(leadId);
    }
  }, [leadId]);

  useEffect(() => {
    if (isOpen && leadId) {
      fetchEmailHistory();
    }
    
    // Cleanup function to clear any pending timeouts
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [isOpen, leadId, fetchEmailHistory]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Get badge variant based on email status
  const getStatusBadge = (status) => {
    switch (status) {
      case 'delivered':
        return <Badge variant="success">Delivered</Badge>;
      case 'opened':
        return <Badge variant="info">Opened</Badge>;
      case 'clicked':
        return <Badge variant="warning">Clicked</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="default">{status || 'sent'}</Badge>;
    }
  };

  // Get badge variant based on email type
  const getTypeBadge = (type) => {
    switch (type) {
      case 'initial_notification':
        return <Badge variant="outline">Initial Notification</Badge>;
      case 'full_details':
        return <Badge variant="success">Full Details</Badge>;
      default:
        return <Badge variant="default">{type || 'Unknown'}</Badge>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MailOpen className="h-5 w-5" />
            Email History for Lead #{leadId} {leadName ? `- ${leadName}` : ''}
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="bg-status-error-bg border border-status-error-border text-status-error-text px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner className="h-8 w-8" />
            <span className="ml-2 text-text-primary">Loading email history...</span>
          </div>
        ) : emailHistory.length === 0 ? (
          <div className="text-center py-8">
            <MailOpen className="h-12 w-12 mx-auto text-text-tertiary" />
            <h3 className="mt-2 text-lg font-medium text-text-primary">No Email History</h3>
            <p className="mt-1 text-text-secondary">No emails have been sent for this lead yet.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date Sent</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Email Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emailHistory.map((email, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDate(email.sent_at)}</TableCell>
                      <TableCell>{email.agent_email}</TableCell>
                      <TableCell>{getTypeBadge(email.email_type)}</TableCell>
                      <TableCell>{getStatusBadge(email.status)}</TableCell>
                      <TableCell>
                        {email.success === false ? (
                          <span className="text-status-error-text">{email.message || 'Failed'}</span>
                        ) : (
                          <span className="text-status-success-text">Success</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Email stats summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-background-secondary p-4 rounded-md border border-border-primary shadow-sm">
                <p className="text-sm text-text-secondary">Initial Notifications</p>
                <p className="text-2xl font-semibold text-text-primary">
                  {emailHistory.filter(email => email.email_type === 'initial_notification').length}
                </p>
              </div>
              <div className="bg-background-secondary p-4 rounded-md border border-border-primary shadow-sm">
                <p className="text-sm text-text-secondary">Full Details</p>
                <p className="text-2xl font-semibold text-text-primary">
                  {emailHistory.filter(email => email.email_type === 'full_details').length}
                </p>
              </div>
              <div className="bg-background-secondary p-4 rounded-md border border-border-primary shadow-sm">
                <p className="text-sm text-text-secondary">Success Rate</p>
                <p className="text-2xl font-semibold text-text-primary">
                  {emailHistory.length ? Math.round((emailHistory.filter(email => email.status !== 'failed').length / emailHistory.length) * 100) : 0}%
                </p>
              </div>
            </div>
          </>
        )}

        <DialogFooter className="flex items-center justify-between mt-4">
          <Button
            variant="outline"
            onClick={() => fetchEmailHistory(true)}
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh History
          </Button>
          <Button variant="primary" onClick={onClose}>
            <XCircle className="mr-2 h-4 w-4" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailHistoryModal;